import { isEmpty } from 'lodash';
import { v4, validate } from 'uuid';
import SubTaskForm from '../../containers/Task/Form/SubTask';
import API from 'services/api';
import { DB_NULL } from '../constants';
import { TaskInfo } from '../constants/informationText';
import { LOCATION_MODE } from '../constants/Map';
import { SCHEDULE_PATTERNS } from '../constants/schedule';
import { serviceTypes } from '../constants/serviceTypes';
import {
  AVAILABLE_EVIDENCE,
  PATROL_AVAILABLE_EVIDENCE,
  TASK_EVIDENCE_TYPE,
  TaskPlaceholder,
} from '../constants/task';
import { transformFormViewSubTask } from '../DataTransform/Task/subTask';
import { omit } from '../methods/helpers';
import { transformList } from 'containers/Pages/Tasks/TaskTypes/Selectors/taskTypeSelectors';

import {
  validateConditionallyIfRequired,
  validateDateRequired,
  validateRequired,
  validateStartDate,
  validateString,
  validateStringNotEmpty,
  validationObject,
} from '../validations';
import { transformAssetList } from '../DataTransform/Asset/AssetList';
import {
  businessClientValidation,
  contactValidation,
  siteBaseValidation,
} from '../validationSchema';
import { EVENT_MAP_ICON_TYPE } from 'utils/constants/Map';
import {
  taskCategoriesBasedOnView,
  transformTaskTypeList,
} from 'utils/DataTransform/Task/taskForm';
import { transformBasicPersonnel } from 'utils/DataTransform/Personnel/PersonnelList';

export const baseTaskValidation = requiredTaskTypeValidation => {
  if (!requiredTaskTypeValidation) {
    return {
      taskName: validateStringNotEmpty(),
      taskDescription: validateString(5000, false),
    };
  }
  return {
    taskName: validateStringNotEmpty(),
    taskType: validateRequired(),
    taskDescription: validateString(5000, false),
  };
};

export const scheduleValidation = ({ pattern }) => {
  const baseValidation = {
    ...validateStartDate(),
    timezone: validateStringNotEmpty(),
  };
  switch (pattern) {
    case SCHEDULE_PATTERNS.simpleInterval.label:
      return validationObject({
        [pattern]: validationObject({
          ...baseValidation,
          interval: validateRequired(),
          finishBefore: validateDateRequired(),
        }),
      });

    case SCHEDULE_PATTERNS.weekly.label:
      return validationObject({
        [pattern]: validationObject({
          startTimeOnly: validateRequired(),
          endTimeOnly: validateRequired(),
          days: validateRequired(),
          startDate: validateRequired(),
          repeatsEvery: validateRequired(),
          timezone: validateRequired(),
        }),
      });
    case SCHEDULE_PATTERNS.dateOfMonth.label:
      return validationObject({
        [pattern]: validationObject({
          startDate: validateRequired(),
          rescheduleWeekends: validateRequired(),
          rescheduleInvalidDates: validateRequired(),
          dateOfMonth: validateRequired(),
          repeatsEvery: validateRequired(),
          startTimeOnly: validateRequired(),
          endTimeOnly: validateRequired(),
          timezone: validateRequired(),
        }),
      });
    case SCHEDULE_PATTERNS.dayOfMonth.label:
      return validationObject({
        [pattern]: validationObject({
          startDate: validateRequired(),
          dayOfMonth: validateRequired(),
          repeatsEvery: validateRequired(),
          days: validateRequired(),
          startTimeOnly: validateRequired(),
          endTimeOnly: validateRequired(),
          timezone: validateRequired(),
        }),
      });
    default:
      return validationObject({ [pattern]: validationObject(baseValidation) });
  }
};

export const taskValidationSchema = ({
  newSite,
  newBusinessClient,
  newContact,
  newSiteContact,
  addSchedule,
  activeSchedule,
  requiredTaskTypeValidation = true,
} = {}) => {
  const validation = {
    ...baseTaskValidation(requiredTaskTypeValidation),
    ...(newSite && {
      ...omit(siteBaseValidation, ['locations']),
      businessClient: validateConditionallyIfRequired({
        conditionKey: 'newBusinessClient',
        conditionValue: false,
      }),
      siteLocations: validateRequired(),
    }),
    ...((newContact || newSiteContact) && { ...contactValidation }),
    ...(newBusinessClient && {
      ...businessClientValidation,
    }),
    ...(addSchedule && {
      schedule: scheduleValidation({ pattern: activeSchedule }),
    }),
  };
  return validationObject(validation);
};

export const mapIconSelector = view => {
  switch (view) {
    case serviceTypes.cleaning:
      return serviceTypes.cleaning; // change back to the required icon when we have the actual cleaning icon
    case serviceTypes.maintenance:
      return serviceTypes.maintenance;
    case serviceTypes.security:
      return serviceTypes.security;
    default:
      return EVENT_MAP_ICON_TYPE.NON_NEWS_EVENT_SINGLE_LOC;
  }
};
export const getFilters = selectedTaskType => {
  return selectedTaskType?.category
    ? { service: [selectedTaskType?.category] }
    : { service: [] };
};
export const TASK_FIELDS = {
  template: ({ onChangeCallback, taskCategory }) => ({
    field: 'copyFromTemplate',
    label: 'Template',
    size: 'full',
    type: 'async-select',
    fetchOptions: API.fetchTaskTemplates,
    fetchFilters: { taskCategory, sort: 'name:asc', isTemplate: true },
    onChangeCallback,
    optionLabelKey: 'name',
    required: true,
  }),
  name: ({ size = 'quarter', disabled = false, ...props }) => ({
    field: 'taskName',
    label: 'Name',
    type: 'text',
    size,
    required: true,
    disabled,
    props,
  }),
  image: {
    field: 'taskImage',
    required: false,
  },
  taskType: ({ size = 'quarter', ...props } = {}) => ({
    field: 'taskType',
    type: 'async-select',
    size: size,
    // disabled: !(options.length > 1),
    fetchOptions: API.fetchTaskTypes,
    dataTransform: ({ data }) => transformTaskTypeList(data),
    optionLabelKey: 'name',
    fetchFilters: {
      archived: props.archived,
      taskCategory: taskCategoriesBasedOnView({
        view: props.view,
        services: props.services,
      }),
    },
    ...props,
  }),
  assignee: ({
    options = [],
    size = 'quarter',
    selectedTaskType,
    ...props
  } = {}) => {
    return {
      field: 'assigneeId',
      label: 'Assignee',
      type: 'async-select',
      size: size,
      fetchOptions: API.fetchUserList,
      dataTransform: ({ data }) => transformBasicPersonnel(data),
      fetchFilters: getFilters(selectedTaskType),
      optionLabelKey: 'name',
      resetWhen: [
        {
          value: selectedTaskType?.category,
          condition: val => {
            return val;
          },
        },
      ],
      options,
      ...props,
    };
  },
  // detailed section
  description: props => ({
    field: 'taskDescription',
    label: 'Description',
    type: 'textArea',
    maxLength: 5000,
    required: false,
    placeholder: TaskPlaceholder.description,
    ...props,
  }),
  schedule: ({
    activeTab = SCHEDULE_PATTERNS.onceOff.label,
    displayView,
    editMode,
    disabled,
  } = {}) => {
    return {
      field: 'schedule',
      type: 'schedule',
      size: 'half',
      activeTab,
      displayView,
      editMode,
      align: 'left',
      disabled,
    };
  },
  evidenceRequired: (patrolEvidenceOptions, showDynamicText, disabled) => ({
    field: 'evidence',
    type: 'sub-form',
    formHeader: 'Add Evidence',
    label: 'Evidence',
    disabled,
    subFields: [
      {
        field: 'type',
        label: 'Type',
        type: 'select',
        customSubForm: true, // this will make sure that setFieldValue of formik is not used
        required: true,
        options: patrolEvidenceOptions
          ? PATROL_AVAILABLE_EVIDENCE
          : AVAILABLE_EVIDENCE,
        size: 'half',
        display: 'display',
        align: 'left',
        updateDependentField: [
          {
            key: 'showDynamicText',
            passValue: true,
            failValue: false,
            condition: value =>
              value === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
              value === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id,
          },
        ],
      },
      {
        field: 'textOptions',
        type: 'dynamic-text-generator',
        required: true,
        hidden: !showDynamicText,
      },
      {
        field: 'description',
        type: 'textArea',
        required: true,
        placeholder: TaskPlaceholder.evidenceDescription,
      },
    ],
    defaultValues: {
      type: '',
      description: '',
      textOptions: '',
    },
    setErrorCondition: [
      {
        // conditions are checked in order
        key: 'textOptions',
        dependentKeys: [
          TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id,
          TASK_EVIDENCE_TYPE.OPTION_SINGLE.id,
        ],
        errorMsg: 'required_label',
        condition: val => {
          // check if textOptions is empty
          return val === '';
        },
      },
      {
        key: 'textOptions',
        dependentKeys: [
          TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id,
          TASK_EVIDENCE_TYPE.OPTION_SINGLE.id,
        ],
        errorMsg: 'text options cannot be same',
        condition: val => {
          if (!val) return false;
          // check if title field in text options are same or not
          const titles = val.map(m => m.title);
          return titles.find((item, index) => titles.indexOf(item) !== index);
        },
      },
    ],
    requiredFields: ['type', 'description'],
  }),
  checkListRequired: props => ({
    field: 'checkList',
    label: 'Checklist',
    buttonLabel: 'evidence_checklist_add_button_label',
    type: 'sub-form',
    formHeader: 'Add Checklist Item',
    ...props,
    subFields: [
      {
        field: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        options: [TASK_EVIDENCE_TYPE.CHECKBOX],
        disabled: true,
        size: 'half',
        display: 'Checkbox',
        align: 'left',
      },
      {
        field: 'description',
        type: 'textArea',
        required: true,
        placeholder: TaskPlaceholder.checkboxDescription,
      },
    ],
    defaultValues: {
      type: 'CHECKBOX',
      description: '',
    },
    requiredFields: ['type', 'description'],
  }),
  location: ({
    siteLocation,
    showPolylines,
    center,
    centerDefault,
    iconTextType,
    // typeOfIcon, // this is of type 'CHECKPOINT' or 'WAYPOINT',
    singleMarkerIcon,
    ...props
  } = {}) => {
    return {
      field: 'location',
      label: 'Location',
      type: 'map-artist',
      requirePan: true,
      maxPoints: 1,
      additionalPolygon: siteLocation,
      additionalPolygonMode: LOCATION_MODE.HIGH_DETAIL,
      singleMarkerIcon,
      disableMarkerClick: true,
      hidePolylines: Boolean(!showPolylines),
      center,
      centerDefault,
      iconTextType,
      ...props,
    };
  },
  imageArtist: ({
    surfaceImageId,
    disabled,
    showPolylines,
    selectedSiteDetailImages,
    singleMarkerIcon,
    typeOfIcon,
  }) => {
    return {
      field: 'location',
      label: 'location',
      type: 'image-artist',
      maxPoints: 1,
      surfaceImageId,
      draggable: false,
      disabled,
      selectedSiteDetailImages,
      singleMarkerIcon,
      typeOfIcon,
      hidePolylines: Boolean(!showPolylines),
    };
  },
  subtasks: ({
    users,
    columns,
    defaultValues,
    taskDetails,
    allowedTaskType,
    hideAssignee,
    omitFields,
    multiSchedule,
    surfaceImageId,
    subTaskSelectedsite = [],
    allowDrag,
    info,
    siteEnabled,
  } = {}) => ({
    type: 'embedded-form',
    field: 'subtasks',
    label: 'Sub Task',
    allowDrag,
    component: SubTaskForm,
    defaultValues,
    componentProps: {
      users,
      allowedTaskType,
      hideAssignee,
      omitFields,
      multiSchedule,
      surfaceImageId,
      subTaskSelectedsite,
      siteEnabled,
    },
    columns,
    dataTransform: data => transformFormViewSubTask(data),
    taskDetails,
    additionalFields: [
      {
        type: 'toggle',
        field: 'subtasksSequentialOrder',
        label: 'Subtasks should be performed in sequential order',
        size: 'half',
        condition: values => values.subtasks && values.subtasks.length > 0,
        info,
      },
    ],
    ...(!multiSchedule && {
      onDeleteCallback: ({ formProps, value }) => {
        if (isEmpty(value.location)) return;
        const dependentValues = formProps.values.additionalMarkers;
        const afterDeleteDependent = dependentValues.filter(
          item => item.id !== value.location[0].id,
        );
        formProps.setFieldValue('additionalMarkers', afterDeleteDependent);
      },
    }),
  }),
  expectedDuration: ({ disabled }) => ({
    id: 'expectedDuration',
    field: 'expectedDuration',
    label: 'Expected Duration',
    fieldType: 'number',
    size: 'quarter',
    type: 'text',
    placeholder: 'Expected time in minutes e.g. 10',
    info: TaskInfo.duration,
    required: false,
    disabled,
  }),
  generateQR: ({ required }) => ({
    type: 'toggle',
    id: 'scanStartOnly',
    field: 'scanStartOnly',
    label: 'Generate QR Code',
    size: 'fifth',
    required,
    updateDependentField: [
      {
        key: 'scanCode',
        value: DB_NULL,
        condition: (value, formValues) =>
          !value && typeof validate(formValues.scanCode),
      },
      {
        key: 'scanCode',
        value: v4(),
        condition: value => value,
      },
    ],
  }),
  linkAssetId: ({
    options = [],
    securityCompanyId,
    archived,
    multiple = false,
    disabled = false,
    ...props
  } = {}) => ({
    field: 'assetId',
    label: 'Assets',
    type: 'async-select',
    fetchOptions: API.getAssetList,
    dataTransform: ({ data }) => transformAssetList(data),
    fetchFilters: {
      securityCompanyId,
      archived,
    },
    multiple: multiple,
    optionLabelKey: 'name',
    options,
    disabled,
    resetWhen: [
      {
        value: true,
        condition: val => val,
      },
    ],
    required: false,
    ...props,
  }),
  asyncTaskTypeFilter: ({ category, companyId }) => ({
    label: 'taskType',
    field: 'taskType',
    type: 'async-select',
    multiple: true,
    useAsyncInFilters: true,
    fetchOptions: API.fetchTaskTypes,
    dataTransform: ({ data }) => {
      return transformList(data);
    },
    fetchFilters: {
      securityCompanyId: companyId,
      archived: false,
      taskCategory: category,
    },
    optionLabelKey: 'name',
  }),
};
